import React, { useState } from 'react';
import {
    useTranslate,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Alert,
    CircularProgress,
    Divider,
    Chip,
} from '@mui/material';
import {
    StopCircle as StopIcon,
    Warning as WarningIcon,
    AccessTime as TimeIcon,
    Bolt as BoltIcon,
} from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';

interface ManualStopDialogProps {
    open: boolean;
    onClose: () => void;
    session: {
        id: string;
        appSessionId: string;
        chargerName?: string;
        siteName?: string;
        status: string;
        energyDelivered?: number;
        currentPower?: number;
        durationSeconds?: number;
    } | null;
}

export const ManualStopDialog: React.FC<ManualStopDialogProps> = ({
    open,
    onClose,
    session,
}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();

    const [adminNotes, setAdminNotes] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>('');

    const resetForm = () => {
        setAdminNotes('');
        setError('');
    };

    const handleSubmit = async () => {
        if (!session) return;

        setSubmitting(true);
        setError('');

        try {
            const payload: any = {
                session_id: session.id,
            };

            // Only include admin notes if provided
            if (adminNotes.trim()) {
                payload.admin_notes = adminNotes.trim();
            }

            const result = await apiClient.post('/api/backend/charging/stop', payload);

            notify('Charging session stopped successfully', { type: 'success' });
            refresh();
            handleClose();
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to stop charging session';
            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            resetForm();
            onClose();
        }
    };

    const formatDuration = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes} min`;
    };

    if (!session) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={submitting}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StopIcon sx={{ color: '#F44336' }} />
                    <Typography variant="h6">Stop Charging Session</Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Warning Alert */}
                    <Alert severity="warning" icon={<WarningIcon />}>
                        <Typography variant="body2">
                            This will send an OCPP RemoteStop command to the charger.
                            The charger will provide the stop reason automatically.
                        </Typography>
                    </Alert>

                    {/* Session Info */}
                    <Box sx={{
                        p: 2,
                        backgroundColor: '#F5F5F5',
                        borderRadius: 1,
                        border: '1px solid #E0E0E0'
                    }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Session Information
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Session ID:
                                </Typography>
                                <Typography variant="body2" fontFamily="monospace">
                                    {session.appSessionId}
                                </Typography>
                            </Box>

                            {session.chargerName && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Charger:
                                    </Typography>
                                    <Typography variant="body2">
                                        {session.chargerName}
                                    </Typography>
                                </Box>
                            )}

                            {session.siteName && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Site:
                                    </Typography>
                                    <Typography variant="body2">
                                        {session.siteName}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Status:
                                </Typography>
                                <Chip
                                    label={session.status}
                                    size="small"
                                    color={session.status === 'charging' ? 'success' : 'default'}
                                />
                            </Box>

                            {session.energyDelivered !== undefined && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Energy Delivered:
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <BoltIcon sx={{ fontSize: 16, color: '#00C853' }} />
                                        <Typography variant="body2" fontWeight="bold">
                                            {session.energyDelivered.toFixed(2)} kWh
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {session.durationSeconds !== undefined && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Duration:
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TimeIcon sx={{ fontSize: 16 }} />
                                        <Typography variant="body2">
                                            {formatDuration(session.durationSeconds)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Divider />

                    {/* Admin Notes (Optional) */}
                    <TextField
                        label="Admin Notes (Optional)"
                        multiline
                        rows={4}
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Add any notes about why this session is being stopped (optional)...\n\nExample: User requested stop due to charging complete earlier than expected."
                        helperText="Optional - Leave blank if no notes needed"
                        fullWidth
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} /> : <StopIcon />}
                    color="error"
                >
                    {submitting ? 'Stopping...' : 'Stop Session'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
